import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import Text from '../../shared/Text';
import { BOT, USER } from './utils/constants';

import { MessageList } from './components/MessageList';
import { MessageInput } from './components/MessageInput';
import { useFetchConversations } from './hooks/useFetchConversations';
import { ScrollContextProvider } from './contexts/ScrollContext';
import Button from '../../shared/Button';
import { ChatHistory } from './components/ChatHistory';
import Tooltip from '../../shared/Tooltip';
import { sendMessageToStream } from './utils/stream';

export default function HrAssistantContainer({ embed = false, assistantUuid }) {
  const { t } = useTranslation('hrAssistants');
  const [conversation, setConversation] = useState({ messages: [], id: null });
  const [botIsTyping, setBotIsTyping] = useState(false);

  const {
    isFetched,
    conversations,
    refetchConversations,
  } = useFetchConversations({ assistantUuid });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const processMessage = useCallback(message => {
    setConversation(data => ({
      ...data,
      id: message.id,
      waitingBot: message.waitingBot,
      messages: [
        ...data.messages,
        { owner: message.owner, text: message.text },
      ],
    }));
  }, []);

  const sendMessage = useCallback(
    newMessage => {
      sendMessageToStream({
        conversationId: conversation.id,
        message: newMessage,
        assistantUuid,
        updateConversation: (message, conversationId) => {
          if (conversation.id === null) {
            refetchConversations();
          }

          setBotIsTyping(true);
          processMessage({
            owner: BOT,
            text: message,
            id: conversationId,
            waitingBot: false,
          });
        },
      });
      processMessage({
        owner: USER,
        text: newMessage,
        waitingBot: true,
      });
    },
    [assistantUuid, conversation.id, processMessage, refetchConversations],
  );

  return (
    <ScrollContextProvider>
      <Tooltip padding="4px 8px" place="top" />
      <Container embed={embed}>
        <ChatBox embed={embed}>
          <Header>
            {!embed && (
              <Button
                width={24}
                height={24}
                padding={0}
                mode="outline"
                variant="black"
                onClick={() => {
                  setIsSidebarOpen(!isSidebarOpen);
                }}
                data-tip={t('sideBarTooltip')}
              >
                <i className="fa-solid fa-sidebar"></i>
              </Button>
            )}
            {!embed && (
              <Button
                width={24}
                height={24}
                padding={0}
                mode="outline"
                variant="black"
                onClick={() => {
                  setConversation({ messages: [], id: null });
                }}
                data-tip={t('newChatTooltip')}
              >
                <i className="fa-solid fa-pen-to-square"></i>
              </Button>
            )}
            <Text as="div" variant="h7" color="black.100">
              {t('header')}
            </Text>
            <Badge variant="small1" color="black.80">
              {t('badge')}
            </Badge>
          </Header>
          <ChatMessages>
            {!embed && (
              <ChatHistory
                isSidebarOpen={isSidebarOpen}
                conversations={conversations}
                setConversation={setConversation}
                activeConversationId={conversation.id}
              />
            )}
            <Content>
              <MessageList
                conversation={conversation}
                setBotIsTyping={setBotIsTyping}
                botIsTyping={botIsTyping}
                embed={embed}
                sendMessage={sendMessage}
                isFetched={isFetched}
              />
              <MessageInput
                embed={embed}
                botIsTyping={botIsTyping}
                conversation={conversation}
                sendMessage={sendMessage}
              />
              <Disclaimer as="div" variant="small3" color="black.80">
                {t('disclaimer')}
              </Disclaimer>
            </Content>
          </ChatMessages>
        </ChatBox>
      </Container>
    </ScrollContextProvider>
  );
}

HrAssistantContainer.propTypes = {
  embed: PropTypes.bool,
  assistantUuid: PropTypes.string,
};

const ChatMessages = styled.div`
  display: flex;
  height: calc(100% - 49px);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  margin: 0 auto;
  ${props =>
    !props.embed &&
    css`
      width: 100%;
      height: calc(100vh - 70px);
      padding: 20px;
    `};
  ${props =>
    props.embed &&
    css`
      max-width: 400px;
      height: 600px;
    `};
`;

const ChatBox = styled.div`
  background: ${themeGet('colors.white')};
  border-radius: 8px;
  border: 1px ${themeGet('colors.black.10')} solid;
  position: relative;
  ${props =>
    !props.embed &&
    css`
      border: none;
      display: flex;
      flex-direction: column;
      height: 100%;
    `};

  ${props =>
    props.embed &&
    css`
      height: 600px;
      display: flex;
      flex-direction: column;
    `};
`;

const Header = styled.div`
  display: flex;
  padding: 12px 12px;
  gap: 4px;
  border-bottom: 1px ${themeGet('colors.black.10')} solid;
`;

const Badge = styled(Text)`
  padding: 4px 10px;
  margin: 0 10px;
  background: ${themeGet('colors.black.10')};
  border-radius: 20px;
`;

const Disclaimer = styled(Text)`
  padding: 10px 24px;
`;
